import { ClientOngoingLoad, OngoingLoadStatus } from '@numeo/types-core'
import type { AppDispatch, RootState } from 'store'
import { preserveCurrentLoadNotes, selectLoadUpdateIndex } from 'store/loadNotes'
import { setDispatchedLoads, setHistoryLoads, setInTransitLoads, setShouldFetchLoads, setUpcomingLoads } from 'store/loadsSlice'
import { mergeLoadUpdate } from './merge-load-update'
import { syncSchedulerForLoad } from './scheduler-sync'
import { shouldIgnoreLoadUpdate } from './should-ignore-load-update'

const matchesLaneFilter = (isLane: boolean | undefined, laneFilterMode: 'all' | 'only' | 'exclude'): boolean => {
    if (laneFilterMode === 'only') return isLane === true
    if (laneFilterMode === 'exclude') return isLane !== true
    return true
}

// ── Batched load update mechanism ────────────────────────────────────────────
// Collects rapid-fire WebSocket load updates and flushes them as a single
// Redux dispatch cycle, preventing cascading re-renders of all 100 visible rows
// on every individual message.
const BATCH_FLUSH_DELAY_MS = 100
const MAX_BATCH_DELAY_MS = 500

interface BatchState {
    pendingLoadUpdates: Map<string | number, ClientOngoingLoad>
    pendingStatusChanges: Map<string | number, { from?: OngoingLoadStatus; to: OngoingLoadStatus }>
    flushTimer: ReturnType<typeof setTimeout> | null
    firstEnqueueTime: number | null
    dispatch: AppDispatch | null
    getState: (() => RootState) | null
}

const batch: BatchState = {
    pendingLoadUpdates: new Map(),
    pendingStatusChanges: new Map(),
    flushTimer: null,
    firstEnqueueTime: null,
    dispatch: null,
    getState: null,
}

// Exported for test cleanup
export function resetLoadUpdateBatch() {
    if (batch.flushTimer) clearTimeout(batch.flushTimer)
    batch.pendingLoadUpdates = new Map()
    batch.pendingStatusChanges = new Map()
    batch.flushTimer = null
    batch.firstEnqueueTime = null
    batch.dispatch = null
    batch.getState = null
}

function flushBatchedLoadUpdates() {
    batch.flushTimer = null
    batch.firstEnqueueTime = null
    if (batch.pendingLoadUpdates.size === 0 || !batch.dispatch || !batch.getState) return

    const dispatch = batch.dispatch
    const state = batch.getState()
    const { laneFilterMode } = state.loads
    let { upcomingLoads, dispatchedLoads, inTransitLoads, historyLoads } = state.loads
    // Without the Dispatched stage a dispatched load is listed on Upcoming, as the server lists it.
    const isDispatchedStageEnabled = state.auth?.featureFlags?.dispatchedStatusEnabled === true
    const listStatus = (status: OngoingLoadStatus): OngoingLoadStatus =>
        status === OngoingLoadStatus.DISPATCHED && !isDispatchedStageEnabled ? OngoingLoadStatus.UPCOMING : status

    const updates = new Map(batch.pendingLoadUpdates)
    // Only a change that moves the load to another list: without the Dispatched stage, Upcoming → Dispatched
    // stays on Upcoming and is updated in place, not dropped and re-added at the bottom.
    const statusChanges = new Map([...batch.pendingStatusChanges].filter(([, change]) => !change.from || listStatus(change.from) !== listStatus(change.to)))
    batch.pendingLoadUpdates = new Map()
    batch.pendingStatusChanges = new Map()

    // First pass: remove loads from old tabs for status changes, track dirty per-tab
    let upcomingDirty = false
    let dispatchedDirty = false
    let inTransitDirty = false
    let historyDirty = false

    for (const [loadId, change] of statusChanges) {
        if (change.from) {
            switch (listStatus(change.from)) {
                case OngoingLoadStatus.UPCOMING:
                    upcomingLoads = upcomingLoads.filter((l) => l.id !== loadId)
                    upcomingDirty = true
                    break
                case OngoingLoadStatus.DISPATCHED:
                    dispatchedLoads = dispatchedLoads.filter((l) => l.id !== loadId)
                    dispatchedDirty = true
                    break
                case OngoingLoadStatus.IN_TRANSIT:
                    inTransitLoads = inTransitLoads.filter((l) => l.id !== loadId)
                    inTransitDirty = true
                    break
                case OngoingLoadStatus.COMPLETED:
                case OngoingLoadStatus.CANCELLED:
                    historyLoads = historyLoads.filter((l) => l.id !== loadId)
                    historyDirty = true
                    break
            }
        }
    }

    // Second pass: apply all updates to the appropriate tab arrays
    for (const [, pendingLoad] of updates) {
        const mergedLoad = preserveCurrentLoadNotes(pendingLoad, state)
        const statusChanged = statusChanges.has(mergedLoad.id)
        switch (listStatus(mergedLoad.status)) {
            case OngoingLoadStatus.UPCOMING: {
                if (statusChanged) {
                    upcomingLoads = [...upcomingLoads.filter((l) => l.id !== mergedLoad.id), mergedLoad]
                } else {
                    upcomingLoads = upcomingLoads.map((l) => (l.id === mergedLoad.id ? mergedLoad : l))
                }
                upcomingDirty = true
                break
            }
            case OngoingLoadStatus.DISPATCHED: {
                if (statusChanged) {
                    dispatchedLoads = [...dispatchedLoads.filter((l) => l.id !== mergedLoad.id), mergedLoad]
                } else {
                    dispatchedLoads = dispatchedLoads.map((l) => (l.id === mergedLoad.id ? mergedLoad : l))
                }
                dispatchedDirty = true
                break
            }
            case OngoingLoadStatus.IN_TRANSIT: {
                if (statusChanged) {
                    inTransitLoads = [...inTransitLoads.filter((l) => l.id !== mergedLoad.id), mergedLoad]
                } else {
                    inTransitLoads = inTransitLoads.map((l) => (l.id === mergedLoad.id ? mergedLoad : l))
                }
                inTransitDirty = true
                break
            }
            case OngoingLoadStatus.COMPLETED: {
                if (statusChanged) {
                    historyLoads = [...historyLoads.filter((l) => l.id !== mergedLoad.id), mergedLoad]
                } else {
                    historyLoads = historyLoads.map((l) => (l.id === mergedLoad.id ? mergedLoad : l))
                }
                historyDirty = true
                break
            }
            case OngoingLoadStatus.CANCELLED:
                // Delivered tab is completed-only; cancelled loads are excluded from tab buckets.
                break
        }

        // Sync Scheduler slice with merged load
        try {
            syncSchedulerForLoad(mergedLoad, dispatch, batch.getState!)
        } catch (e) {
            console.error('Scheduler sync (LOAD_UPDATED) skipped:', (e as Error)?.message)
        }
    }

    // Single batch of dispatches — React batches these into one render
    dispatch(setShouldFetchLoads(false))
    if (upcomingDirty) dispatch(setUpcomingLoads(upcomingLoads.filter((l) => matchesLaneFilter(l.isLane, laneFilterMode))))
    if (dispatchedDirty) dispatch(setDispatchedLoads(dispatchedLoads.filter((l) => matchesLaneFilter(l.isLane, laneFilterMode))))
    if (inTransitDirty) dispatch(setInTransitLoads(inTransitLoads.filter((l) => matchesLaneFilter(l.isLane, laneFilterMode))))
    if (historyDirty) dispatch(setHistoryLoads(historyLoads.filter((l) => matchesLaneFilter(l.isLane, laneFilterMode))))
}

export function enqueueLoadUpdate(load: Partial<ClientOngoingLoad> & { id: string | number }, dispatch: AppDispatch, getState: () => RootState) {
    batch.dispatch = dispatch
    batch.getState = getState

    const state = getState()
    const index = selectLoadUpdateIndex(state)
    // Prefer a pending update, then the primary load lists, then a scheduler-only row.
    const existingPending = batch.pendingLoadUpdates.get(load.id)
    const existingInRedux = index.main.get(load.id)
    const existing = existingPending || index.all.get(load.id)
    const base = existing ? preserveCurrentLoadNotes(existing, state) : undefined

    if (shouldIgnoreLoadUpdate(base, load)) return

    const mergedLoad: ClientOngoingLoad = base ? mergeLoadUpdate(base, load) : (load as ClientOngoingLoad)
    const previousStatus = existingInRedux?.status
    const statusChanged = load.status !== undefined && previousStatus !== undefined && load.status !== previousStatus

    batch.pendingLoadUpdates.set(mergedLoad.id, mergedLoad)
    if (statusChanged && previousStatus) {
        batch.pendingStatusChanges.set(mergedLoad.id, { from: previousStatus, to: mergedLoad.status })
    }

    // Force flush if we've been batching too long (prevents starvation)
    if (!batch.firstEnqueueTime) batch.firstEnqueueTime = Date.now()
    if (Date.now() - batch.firstEnqueueTime >= MAX_BATCH_DELAY_MS) {
        if (batch.flushTimer) clearTimeout(batch.flushTimer)
        flushBatchedLoadUpdates()
        return
    }

    // Reset the debounce timer — coalesce updates arriving within the window
    if (batch.flushTimer) clearTimeout(batch.flushTimer)
    batch.flushTimer = setTimeout(flushBatchedLoadUpdates, BATCH_FLUSH_DELAY_MS)
}
